import { resolveDeployEnvironment } from '@/lib/config/deploy-environment';
import { publicEnv } from '@/lib/config/public-env';
import { withCreditClaims } from '@/@credit-consumption/claims';
import { UserRole } from '@/@portal/types';
import { Routes } from '@/app/routes';

/**
 * Gets the appropriate cookie domain based on environment
 */
function getCookieDomain(): string {
    return publicEnv('NEXT_PUBLIC_COOKIE_DOMAIN') || '.mfourstudio.com';
}

/**
 * The session cookie's name. In development each server gets its own, suffixed with its port.
 *
 * Every local dev server shares one `localhost` cookie jar, and the servers use different
 * NEXTAUTH_SECRETs. A signed-in session is about 6 KB, so next-auth splits it into numbered parts
 * (`.0`, `.1`, ...). With one shared name, a part another server left behind is read back with
 * this server's parts, nothing decrypts, and signing in loops straight back to the sign-in page.
 *
 * Deployed builds share one cookie domain (`.mfourstudio.com`), so test1, test2, staging and
 * production would all read and write the same cookie: signing in to one signed the others out
 * (UAT 2026-11-02). Each test environment and staging get their own name, decided from the
 * server's own address (NEXTAUTH_URL), never the request. Production keeps the shared name.
 */
export function sessionCookieName(env: NodeJS.ProcessEnv = process.env): string {
    if (env.NODE_ENV === 'development' && env.PORT) return `next-auth.session-token-${env.PORT}`;
    let hostname: string | undefined;
    try {
        hostname = env.NEXTAUTH_URL ? new URL(env.NEXTAUTH_URL).hostname : undefined;
    } catch {
        hostname = undefined;
    }
    const deploy = hostname ? resolveDeployEnvironment({ hostname }).environment : null;
    if (deploy === 'test1' || deploy === 'test2' || deploy === 'staging')
        return `next-auth.session-token-${deploy}`;
    return 'next-auth.session-token';
}

/**
 * Authentication configuration
 */
export const AUTH_CONFIG = {
    /** Cookie configuration */
    cookies: {
        /** Cookie options for authentication tokens */
        options: {
            /** Prevents JavaScript access to the cookie */
            httpOnly: false,
            /** Only sends cookie over HTTPS in production */
            secure: process.env.NODE_ENV === 'production',
            /** Cookie's same-site policy */
            sameSite: 'lax' as const,
            /** Domain scope for the cookie */
            domain: getCookieDomain(),
            /** Cookie path */
            path: '/',
        },
    },
    /** Token configuration */
    tokens: {
        /** Access token expiration time in seconds */
        accessTokenExpiry: 36000, // 10 hours (matches Cognito ID token duration)
        /** Refresh token expiration time in seconds */
        refreshTokenExpiry: 30 * 24 * 60 * 60, // 30 days
        /** Time before token expiry to trigger refresh (in seconds) */
        refreshBeforeExpiry: 30 * 60, // 30 minutes
    },
    /** Rate limiting configuration */
    rateLimit: {
        /** Rate limit window in milliseconds */
        window: 60 * 1000, // 1 minute
        /** Maximum requests per window */
        maxRequests: 5,
        /** Maximum unique tokens per interval */
        uniqueTokens: 500,
    },
    /** Protected routes configuration */
    routes: {
        /** Routes that require authentication */
        protected: ['/portal', '/account', '/feasibility'],
        /** Public routes */
        public: [Routes.SIGNIN, Routes.SIGNUP, Routes.FORGOT_PASSWORD, Routes.RESET_PASSWORD],
        /** Routes that require CSRF protection */
        csrfProtected: ['/api/auth'],
    },
    /** Development mode configuration */
    development: {
        /** Mock user for development */
        mockUser: {
            id: 'local-dev-id',
            email: 'local.dev@mfour.com',
            groups: withCreditClaims([
                UserRole.INTERNAL,
                UserRole.ADMIN,
                UserRole.DEVELOPER,
                UserRole.CANARY,
            ]),
            role: UserRole.DEVELOPER,
            isAdmin: true,
            isInternal: true,
            salesforceOrgId: '001d000002BBDTKAA5',
            salesforceOrg: 'MFour Marketing',
            stripeCustomerId: publicEnv('NEXT_PUBLIC_STRIPE_CUSTOMER_ID') || '',
            name: 'Local Developer',
            givenName: 'Local',
            familyName: 'Developer',
            hasStudioAIAccess: true,
            isUnaffiliated: false,
            hasSubsidiaryAccess: false,
            crossSubsidiaryWriteEnabled: true,
            parentOrgId: null,
            subsidiaryRole: 'standalone' as const,
            subsidiaryOrgIds: [],
            subsidiarySfAccountIds: [],
            freemiumUsage: {
                hasReachedLimit: true,
                currentCount: 10,
                limit: 10,
                limitType: 'message',
                daysUntilReset: 1,
            },
        },
        /** Mock organization profile for development */
        mockOrgProfile: {
            orgId: 2,
            orgName: 'MFour Marketing',
            sfAccountId: '001d000002BBDTKAA5',
            sigmaConnectionId: '93b0e686-c7f8-4f3f-b0ab-73c294ccda08',
            stripeCustomerId: 'cus_SYMc5srN5l8MOv',
            isLicenseValid: true,
            licenseEndDate: new Date('4000-01-01T21:26:58.000Z'),
            contactEmail: 'local.dev@mfour.com',
            hasClientStudioAIAccess: true,
        },
        /** Mock team member for development */
        mockTeamMember: {
            userId: 1,
            email: 'local.dev@mfour.com',
            firstName: 'Local',
            lastName: 'Developer',
            stripeCustomerId: 'cus_Tjqqj7ZZHb6KNH',
        },
    },
} as const;
